<template>
  <div class="phone-number-input-wrapper">
    <VuePhoneNumberInput
      v-model="internalValue"
      :default-country-code="currentCountryCode"
      :ignored-countries="ignoredCountries"
      :no-validator-state="!showValidationState"
      :size="size"
      :disabled="disabled"
      :translations="translations"
      :no-example="false"
      v-bind="inputAttrs"
      @update="onUpdate"
    />
    <a-alert
      v-if="validationError"
      class="phone-input-alert"
      type="error"
      :message="validationError"
      show-icon
    />
    <!-- <div v-if="descriptionText" class="phone-input-description">
      {{ descriptionText }}
    </div> -->
  </div>
</template>

<script>
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const TN_NATIONAL_LENGTH = 8;
const KE_NATIONAL_LENGTH = 10;

/**
 * Parse an E.164 phone string and return the ISO country code and national number.
 * Returns null when parsing fails.
 */
function parseE164(e164) {
  if (!e164 || typeof e164 !== "string" || !e164.startsWith("+")) return null;
  try {
    const parsed = parsePhoneNumberFromString(e164);
    if (parsed && parsed.country) {
      return { countryCode: parsed.country, nationalNumber: parsed.nationalNumber || "" };
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export default {
  name: "PhoneNumberInput",
  components: { VuePhoneNumberInput },
  props: {
    value: {
      type: String,
    },
    defaultCountryCode: {
      type: String,
      default: "TN",
    },
    showValidationState: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // If the initial value is E.164, detect the country and extract national number
    const initial = this.value || "";
    const parsed = parseE164(initial);
    return {
      internalValue: parsed ? parsed.nationalNumber : initial,
      currentCountryCode: parsed ? parsed.countryCode : this.defaultCountryCode,
      isCurrentlyValid: false,
      validationError: "",
      ignoredCountries: ["IL"],
      echoPending: false,
    };
  },
  computed: {
    translations() {
      return {
        countrySelectorLabel: this.$t("phoneInput.countryCodeLabel"),
        countrySelectorError: "",
        phoneNumberLabel: "",
        example: this.$t("phoneInput.example"),
      };
    },
    descriptionText() {
      if (this.currentCountryCode === "TN") {
        return this.$t("phoneInput.descriptionTN");
      }
      if (this.currentCountryCode === "KE") {
        return this.$t("phoneInput.descriptionKE");
      }
      return "";
    },
    inputMaxLength() {
      if (this.currentCountryCode === "TN") return TN_NATIONAL_LENGTH;
      if (this.currentCountryCode === "KE") return KE_NATIONAL_LENGTH;
      return undefined;
    },
    inputMaxLengthAttr() {
      if (this.currentCountryCode === "TN") return TN_NATIONAL_LENGTH + 2;
      if (this.currentCountryCode === "KE") return KE_NATIONAL_LENGTH + 2;
      return undefined;
    },
    inputAttrs() {
      const attrs = {
        inputmode: "numeric",
        autocomplete: "tel",
      };
      if (this.inputMaxLengthAttr != null) {
        // `vue-phone-number-input` forwards this to the actual <input>.
        attrs.maxlength = this.inputMaxLengthAttr;
      }
      return attrs;
    },
  },
  watch: {
    value(newVal) {
      const v = newVal == null ? "" : String(newVal).trim();
      if (!v) {
        // Ignore the echo of our own "" emitted for a lone, still-unparseable digit.
        if (this.echoPending) return;
        this.internalValue = "";
        return;
      }
      // If the new value is E.164, detect the country and use only the national number
      const parsed = parseE164(v);
      if (parsed) {
        this.currentCountryCode = parsed.countryCode;
        if (parsed.nationalNumber !== this.internalValue) {
          this.internalValue = parsed.nationalNumber;
        }
      } else if (v !== this.internalValue) {
        this.internalValue = v;
      }
    },
  },
  methods: {
    onUpdate(payload) {
      if (!payload) {
        this.currentCountryCode = this.defaultCountryCode;
        this.validationError = "";
        this.$emit("change", "");
        this.$emit("raw-change", "");
        return;
      }
      // Some versions of `vue-phone-number-input` may emit an empty `countryCode`
      // while typing. Fall back to the last known country or default.
      const effectiveCountryCode = String(
        payload.countryCode || this.currentCountryCode || this.defaultCountryCode || ""
      ).toUpperCase();
      if (payload.countryCode) this.currentCountryCode = effectiveCountryCode;

      // Validation only in numbers: keep digits only (no spaces, no dashes, no letters).
      const nationalNumber = payload.nationalNumber ? String(payload.nationalNumber).replace(/\D/g, "") : "";
      const isValid = payload.isValid === true;

      const requiredLength =
        effectiveCountryCode === "TN"
          ? TN_NATIONAL_LENGTH
          : effectiveCountryCode === "KE"
            ? KE_NATIONAL_LENGTH
            : undefined;

      if (requiredLength != null && nationalNumber.length > requiredLength) {
        const truncatedDigits = nationalNumber.slice(0, requiredLength);
        this.$nextTick(() => {
          this.internalValue = truncatedDigits;
        });
        return;
      }

      const cappedNationalNumber = nationalNumber;

      // Build E.164 without spaces so backend receives digits-only (with + prefix).
      let e164 =
        payload.e164 ||
        (payload.countryCallingCode && cappedNationalNumber ? `+${payload.countryCallingCode}${cappedNationalNumber}` : "") ||
        "";
      e164 = String(e164).replace(/\s/g, "").trim();

      // Always provide the current E.164 (or "") for consumers that need it even before strict validation.
      this.$emit("raw-change", e164);

      // Validate by numbers only and required length (TN: 8 digits, KE: 10 digits).
      let finalValid = isValid;
      if (effectiveCountryCode === "TN") {
        finalValid = isValid && cappedNationalNumber.length === TN_NATIONAL_LENGTH;
      } else if (effectiveCountryCode === "KE") {
        finalValid = isValid && cappedNationalNumber.length === KE_NATIONAL_LENGTH;
      }

      // Show an explicit alert when the user has started typing but the digit length is not correct.
      // (Spaces are ignored since we validate with digits-only.)
      if (requiredLength != null && cappedNationalNumber.length > 0 && cappedNationalNumber.length !== requiredLength) {
        this.validationError =
          effectiveCountryCode === "TN"
            ? (this.$t ? this.$t("requis.telephone") : "Tunisian number should be 8 digits")
            : effectiveCountryCode === "KE"
              ? (this.$t ? this.$t("requis.telephoneKE") : "Kenyan number should be 10 digits")
              : "";
      } else {
        this.validationError = "";
      }

      // Always emit the e164 value so the input is never cleared while typing.
      // Form-level validation (in saveBuildingEdit, etc.) must check digit count
      // for TN/KE before saving.
      if (!e164 && payload.phoneNumber) {
        this.echoPending = true;
        this.$nextTick(() => {
          this.echoPending = false;
        });
      }
      this.$emit("change", e164);

      // Store validation state for potential future use
      this.isCurrentlyValid = finalValid;
    },
  },
};
</script>

<style scoped>
.phone-number-input-wrapper {
  width: 100%;
  position: relative;
}

.phone-input-alert {
  margin-top: 6px;
}

.phone-input-description {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 4px;
  line-height: 1.4;
  position: absolute;
  left: 0;
  width: 120px;
  z-index: 1;
}

/* Reduce padding inside the phone input and country selector */
::v-deep .vue-phone-number-input .input-phone-number input,
::v-deep .vue-phone-number-input .input-tel__input {
  padding: 4px 8px !important;
}

::v-deep .vue-phone-number-input .select-country-container,
::v-deep .vue-phone-number-input .country-selector,
::v-deep .vue-phone-number-input .input-country-selector {
  padding: 0 !important;
  padding-top: 0 !important;
  min-height: 32px;
}

::v-deep .vue-phone-number-input .country-selector__input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

::v-deep .vue-phone-number-input .country-selector__label {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

::v-deep .vue-phone-number-input .country-selector__country-flag {
  top: 14px !important;
}

/* Ensure country list overlays helper text + sibling inputs */
::v-deep .vue-phone-number-input .select-country-container {
  position: relative;
}

::v-deep .vue-phone-number-input .country-selector {
  position: relative;
  z-index: 1;
}

::v-deep .vue-phone-number-input .country-selector.has-list-open {
  z-index: 3000;
}

::v-deep .vue-phone-number-input .country-selector__list {
  z-index: 4000 !important;
}

/* Hide all validation text messages and error texts */
::v-deep .vue-phone-number-input .input-tel__label,
::v-deep .vue-phone-number-input .input-phone-number__label,
::v-deep .vue-phone-number-input .country-selector__label:not(.country-selector__label--show-code),
::v-deep .vue-phone-number-input .select-country-container__label,
::v-deep .vue-phone-number-input .input-tel__error,
::v-deep .vue-phone-number-input .input-phone-number__error,
::v-deep .vue-phone-number-input .text-danger,
::v-deep .vue-phone-number-input .error-message,
::v-deep .vue-phone-number-input .field-error,
::v-deep .vue-phone-number-input .help-text {
  display: none !important;
}

/* Keep red border on invalid inputs */
::v-deep .vue-phone-number-input.is-invalid .input-phone-number input,
::v-deep .vue-phone-number-input.is-invalid .input-tel__input {
  border-color: #ff0000 !important;
}

::v-deep .vue-phone-number-input.is-invalid .select-country-container,
::v-deep .vue-phone-number-input.is-invalid .country-selector,
::v-deep .vue-phone-number-input.is-invalid .input-country-selector {
  border-color: #ff0000 !important;
}

::v-deep .vue-phone-number-input.flex {
  display: flex;
  align-items: stretch;
}

::v-deep .vue-phone-number-input .flex-1 {
  flex: 1;
  min-width: 0;
}
</style>
